import { PushNotificationSchema } from '@capacitor/push-notifications';
import dayjs from 'dayjs';
import { UUID } from '../../entities/uuid.model';
import { INotification } from './notification.model';

export class NotificationMission implements INotification {
  constructor(
    public readonly title: string | undefined,
    public readonly body: string | undefined,
    public readonly idDemande: UUID
  ) {}

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationMission {
    const notificationTypes = {
      [NotificationMissionNouvelleDemande.NAME]: NotificationMissionNouvelleDemande,
      [NotificationMissionPrecisionClient.NAME]: NotificationMissionPrecisionClient,
      [NotificationFinMissionSansGardiennage.NAME]: NotificationFinMissionSansGardiennage,
      [NotificationFinMissionAvecGardiennage.NAME]: NotificationFinMissionAvecGardiennage,
      [NotificationClotureAttendAutreAgent.NAME]: NotificationClotureAttendAutreAgent,
      [NotificationNouveauGardiennageRejointAgent.NAME]: NotificationNouveauGardiennageRejointAgent,
      [NotificationMissionUpdateSiteClient.NAME]: NotificationMissionUpdateSiteClient,
    };

    const NotificationType = notificationTypes[extra.type as keyof typeof notificationTypes];
    if (NotificationType) {
      return NotificationType.hydrate(notification, extra);
    }

    console.warn('Unknown notification type:', notification, extra);
    return NotificationDemandeUnknown.hydrate(notification, extra);
  }
}

export class NotificationMissionNouvelleDemande extends NotificationMission {
  public static readonly NAME = 'NOUVELLE_DEMANDE';

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationMissionNouvelleDemande {
    return new NotificationMissionNouvelleDemande(notification.title as string, notification.body as string, extra.idDemande);
  }
}

export class NotificationMissionUpdateSiteClient extends NotificationMission {
  public static readonly NAME = 'UPDATESITE';

  constructor(title: string, body: string, idDemande: UUID) {
    super(title, body, idDemande);
  }

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationMissionUpdateSiteClient {
    return new NotificationMissionUpdateSiteClient(notification.title as string, notification.body as string, extra.idDemande);
  }
}

export class NotificationMissionPrecisionClient extends NotificationMission {
  public static readonly NAME = 'PRECISION';
  public readonly instant: Date;

  constructor(title: string, body: string, idDemande: UUID, instant: Date) {
    super(title, body, idDemande);
    this.instant = instant;
  }

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationMissionPrecisionClient {
    return new NotificationMissionPrecisionClient(
      notification.title as string,
      notification.body as string,
      extra.idDemande,
      extra.instant
    );
  }
}

export class NotificationFinMissionSansGardiennage extends NotificationMission {
  public static readonly NAME = 'CLOTURE_SANS_GARDIENNAGE';

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationFinMissionSansGardiennage {
    return new NotificationFinMissionSansGardiennage(notification.title as string, notification.body as string, extra.idDemande);
  }
}

// // Quand l'agent sur place est celui qui doit prendre le gardiennage
export class NotificationFinMissionAvecGardiennage extends NotificationMission {
  public static readonly NAME = 'CLOTURE_AVEC_GARDIENNAGE';
  constructor(
    title: string,
    body: string,
    idDemande: UUID,
    public readonly debut: dayjs.Dayjs,
    public readonly fin: dayjs.Dayjs,
    public readonly idCreneauGardiennage: UUID,
    public readonly consigne: string
  ) {
    super(title, body, idDemande);
  }

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationFinMissionAvecGardiennage {
    return new NotificationFinMissionAvecGardiennage(
      notification.title as string,
      notification.body as string,
      extra.idDemande,
      dayjs(extra.debut),
      dayjs(extra.fin),
      extra.idCreneauGardiennage,
      extra.consigne
    );
  }
}

export class PhotoDTO {
  constructor(
    public readonly containPersonalData: string,
    public readonly small: string,
    public readonly large: string
  ) {}

  public static hydrate(obj: PhotoDTO): PhotoDTO {
    return new PhotoDTO(obj.containPersonalData, obj.small, obj.large);
  }
}

export class AgentDTO {
  constructor(
    public readonly id: UUID,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly photo?: PhotoDTO,
    public readonly cnaps?: string
  ) {}

  public static hydrate(obj: AgentDTO): AgentDTO {
    return new AgentDTO(obj.id, obj.firstName, obj.lastName, obj.photo ? PhotoDTO.hydrate(obj.photo) : undefined, obj.cnaps);
  }
}

// Quand l'agent sur place n'est pas celui qui doit prendre le gardiennage
export class NotificationClotureAttendAutreAgent extends NotificationMission {
  public static readonly NAME = 'CLOTURE_ATTEND_AUTRE_AGENT';
  constructor(
    title: string,
    body: string,
    idDemande: UUID,
    public readonly debut: dayjs.Dayjs,
    public readonly fin: dayjs.Dayjs,
    public readonly idCreneauGardiennage: UUID,
    public readonly consigne: string,
    public readonly agent?: AgentDTO
  ) {
    super(title, body, idDemande);
  }

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationClotureAttendAutreAgent {
    return new NotificationClotureAttendAutreAgent(
      notification.title as string,
      notification.body as string,
      extra.idDemande,
      dayjs(extra.debut),
      dayjs(extra.fin),
      extra.idCreneauGardiennage,
      extra.consigne,
      extra.agent ? AgentDTO.hydrate(JSON.parse(extra.agent)) : undefined
    );
  }
}

// Quand on affecte un autre agent au gardiennage et qu'il doit rejointdre l'agent sur place
export class NotificationNouveauGardiennageRejointAgent extends NotificationMission {
  public static readonly NAME = 'REJOINT_AGENT';
  constructor(
    title: string,
    body: string,
    idDemande: UUID,
    public readonly debut: dayjs.Dayjs,
    public readonly fin: dayjs.Dayjs,
    public readonly idCreneauGardiennage: UUID,
    public readonly consigne: string,
    public readonly agent?: AgentDTO
  ) {
    super(title, body, idDemande);
  }

  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationNouveauGardiennageRejointAgent {
    return new NotificationNouveauGardiennageRejointAgent(
      notification.title as string,
      notification.body as string,
      extra.idDemande,
      dayjs(extra.debut),
      dayjs(extra.fin),
      extra.idCreneauGardiennage,
      extra.consigne,
      extra.agent ? AgentDTO.hydrate(JSON.parse(extra.agent)) : undefined
    );
  }
}

export class NotificationDemandeUnknown extends NotificationMission {
  public static hydrate(notification: PushNotificationSchema, extra: any): NotificationDemandeUnknown {
    return new NotificationDemandeUnknown(notification.title as string, notification.body as string, extra.idDemande);
  }
}
